import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useAccount } from 'wagmi';
import { toast } from "react-toastify";
import { useExtendDuration } from '../hooks/useExtendDuration';
import Loader from '../../../../shared/Loader';
import { ApiUrl } from '../../../../utils/ApiUrl';
import { usePoolUpdate } from '../hooks/usePoolUpdate';
import { useTheme } from '../../../../hooks/ThemeProvider';
import { EXPLORER_URL } from '../../../../utils/constant';

function ExtendDurationModals({ show, onHide, poolData, fetchRootPools }) {
    const { address: account } = useAccount();
    const { extendDuration, estimateExtendGas, isExtendInProgress, isEstimatingGas } = useExtendDuration();
    const { updatePoolEndDate, isUpdating } = usePoolUpdate();

    const [currentStep, setCurrentStep] = useState(1);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [gasEstimate, setGasEstimate] = useState(null);
    const [transactionHash, setTransactionHash] = useState('');

    // Time Picker State
    const [hour, setHour] = useState(12);
    const [minute, setMinute] = useState(0);
    const [meridiem, setMeridiem] = useState("AM");

    // Set minimum date when modal opens
    useEffect(() => {
        if (show && poolData?.duration) {
            const currentEndDate = new Date(poolData.duration * 1000);
            const minDate = new Date(currentEndDate.getTime() + 24 * 60 * 60 * 1000);
            setSelectedDate(minDate);
            // Reset time picker to default
            setHour(12);
            setMinute(0);
            setMeridiem("AM");
        }
    }, [show, poolData?.duration]);

    // Helper function to apply time to date
    const applyTimeToDate = (date, h, m, mer) => {
        const newDate = new Date(date);
        const hour24 = mer === "PM" && h !== 12 ? h + 12 : (mer === "AM" && h === 12 ? 0 : h);
        newDate.setHours(hour24, m, 0, 0);
        return newDate;
    };

    // Get selected date with time applied
    const getDateWithTime = () => {
        return applyTimeToDate(selectedDate, hour, minute, meridiem);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setSelectedDate(new Date());
        setGasEstimate(null);
        setTransactionHash('');
        setHour(12);
        setMinute(0);
        setMeridiem("AM");
        onHide();
    };

    // Get current end date from pool data
    const getCurrentEndDate = () => {
        if (!poolData?.duration) return null;
        return new Date(poolData.duration * 1000);
    };

    // Get minimum selectable date (current end date + 1 second)
    const getMinSelectableDate = () => {
        const currentEndDate = getCurrentEndDate();
        if (!currentEndDate) return new Date();
        return new Date(currentEndDate.getTime() + 1000);
    };

    // Calculate new end time as Unix timestamp (for display)
    const calculateNewEndTimestamp = () => {
        return Math.floor(getDateWithTime().getTime() / 1000);
    };

    // Calculate time to add in seconds (for contract)
    const calculateTimeToAddInSeconds = () => {
        const currentEndDate = getCurrentEndDate();
        if (!currentEndDate) return 0;

        const timeDiff = Math.floor((getDateWithTime().getTime() - currentEndDate.getTime()) / 1000);
        return Math.max(0, timeDiff);
    };

    // Calculate time difference for display purposes
    const calculateTimeDifference = () => {
        return calculateTimeToAddInSeconds();
    };

    // Validate date selection
    const validateDate = () => {
        const currentEndDate = getCurrentEndDate();

        if (!currentEndDate) {
            toast.error("Pool end date not available");
            return false;
        }

        const dateWithTime = getDateWithTime();
        if (dateWithTime <= currentEndDate) {
            toast.error("New end date must be after the current end date");
            return false;
        }

        const timeToAdd = calculateTimeToAddInSeconds();
        if (timeToAdd <= 0) {
            toast.error("Please select a valid future date");
            return false;
        }

        return true;
    };

    const handleConfirm = async () => {
        if (!validateDate()) return;

        try {
            const increaseTimeInSeconds = calculateTimeToAddInSeconds();

            // Estimate gas
            const estimate = await estimateExtendGas(
                poolData?.contractAddress,
                increaseTimeInSeconds
            );
            setGasEstimate(estimate);
            setCurrentStep(2);
        } catch (error) {
            console.error("Gas estimation error:", error);
            toast.error("Failed to estimate gas fees. Please try again.");
        }
    };

    const handleFinalConfirm = async () => {
        if (!account) {
            toast.error("Please connect your wallet");
            return;
        }

        try {
            const increaseTimeInSeconds = calculateTimeToAddInSeconds();

            const result = await extendDuration(
                poolData?.contractAddress,
                increaseTimeInSeconds
            );

            // Update pool duration via API with time-applied date
            await updatePoolEndDate(poolData?._id, getDateWithTime());

            setTransactionHash(result.hash);
            fetchRootPools()
            setCurrentStep(3);

        } catch (error) {
            console.error("Duration extension error:", error);
            if (error.message?.includes("User rejected")) {
                toast.error("Transaction rejected by user");
            } else {
                toast.error("Failed to extend duration. Please try again.");
            }
        }
    };

    const handleFinalClose = () => {
        handleClose();
    };

    // Format date helper
    const formatDateTime = (date) => {
        if (!date) return "N/A";
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "2-digit",
        }) + ' ' + date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Get previous end date from pool data
    const getPreviousEndDate = () => {
        const currentEndDate = getCurrentEndDate();
        return currentEndDate ? formatDateTime(currentEndDate) : "N/A";
    };

    // Calculate days being added
    const getDaysAdded = () => {
        const timeDifference = calculateTimeDifference();
        const days = Math.floor(timeDifference / (24 * 60 * 60));
        const hours = Math.floor((timeDifference % (24 * 60 * 60)) / 3600);

        if (days > 0 && hours > 0) {
            return `${days} day${days !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
        } else if (days > 0) {
            return `${days} day${days !== 1 ? 's' : ''}`;
        } else {
            return `${hours} hour${hours !== 1 ? 's' : ''}`;
        }
    };

    // Increase/Decrease hour
    const increaseHour = () => setHour(prev => (prev === 12 ? 1 : prev + 1));
    const decreaseHour = () => setHour(prev => (prev === 1 ? 12 : prev - 1));

    // Increase/Decrease minute
    const increaseMinute = () => setMinute(prev => (prev === 59 ? 0 : prev + 1));
    const decreaseMinute = () => setMinute(prev => (prev === 0 ? 59 : prev - 1));

    // Toggle AM/PM
    const toggleMeridiem = () => setMeridiem(prev => (prev === "AM" ? "PM" : "AM"));

    const truncateHash = (hash) => {
        if (!hash) return "0x74rhiw4rf38u...5r2";
        return `${hash.slice(0, 10)}...${hash.slice(-3)}`;
    };

    const isProcessing = isExtendInProgress || isEstimatingGas;

    const getLoadingText = () => {
        if (isExtendInProgress) return 'Extending Duration...';
        if (isEstimatingGas) return 'Estimating Gas Fees...';
        return 'Loading...';
    };

    const { theme } = useTheme();

    return (
        <>
            {/* Show loader when processing */}
            {isProcessing && <Loader loadingText={getLoadingText()} />}

            {/* Step 1: Extend Duration Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 1 && !isProcessing}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Extend Duration</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="reward_modal">
                        <div className="amount">
                            <p>Select New End Date</p>
                            <span className="greeyyy">
                                Current End: <span className="whitetext">
                                    {getPreviousEndDate()}
                                </span>
                            </span>

                        </div>

                    </div>
                    <div className="date_pickerrr newpicker">
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => setSelectedDate(date)}
                            inline
                            minDate={getMinSelectableDate()}
                        />
                    </div>
                    <div className='lastday_try'>
                        <div className="endtime_sec mysideee">
                            <span>End Time</span>
                            <div className="timerssec">
                                <div className="hour">
                                    <p>{hour.toString().padStart(2, "0")}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={increaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={increaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={decreaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={decreaseHour} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                <h3>:</h3>

                                <div className="min">
                                    <p>{minute.toString().padStart(2, "0")}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={increaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={increaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={decreaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={decreaseMinute} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                <div className="am-and-pm">
                                    <p>{meridiem}</p>
                                    <div className="svgstimes">
                                        {theme === 'dark' ? (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08336L4.55577 3.13892L2.61133 5.08336" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 5.08324L4.55577 3.13879L2.61133 5.08324" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                        {theme === 'dark' ? (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91664L4.55577 4.86108L2.61133 2.91664" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg onClick={toggleMeridiem} xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                                <path d="M6.50022 2.91676L4.55577 4.86121L2.61133 2.91676" stroke="#000F2F" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="buton_modal_div">
                        <button
                            onClick={handleConfirm}
                            disabled={isProcessing}
                        >
                            {isEstimatingGas ? "Estimating..." : "Confirm"}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 2: Finalize Extend Duration Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 2 && !isProcessing}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Finalize Extend Duration</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="modal_share">
                        <div className="inner_type">
                            <div className="one_modal">
                                <p>New End Date</p>
                                <h3>{formatDateTime(getDateWithTime())}</h3>
                                <p className="numbers">+{getDaysAdded()}</p>
                            </div>
                            <div className="main_lock">
                                <div className="inner_lock">
                                    <p>Previous End Date</p>
                                    <span>{getPreviousEndDate()}</span>
                                </div>
                                <div className="inner_lock">
                                    <p>Gas Fees</p>
                                    <span>
                                        $0.00 ({gasEstimate?.totalGasCostETHFormatted || '0'} ETH)
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="buton_modal_div">
                            <button
                                onClick={handleFinalConfirm}
                                disabled={isProcessing}
                            >
                                {isExtendInProgress ? "Processing..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 3: Duration Extended Success Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 3 && !isProcessing}
                onHide={handleFinalClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="succes_modal">
                        <div className="top_success">
                            <img src="/assets/tick.svg" alt="success" />
                            <h3>Duration Extended</h3>
                            <p>Your pool's staking duration has been updated successfully.</p>
                        </div>
                        <div className="hash_div">
                            <div>
                                <p>Transaction Hash</p>
                                <span>{truncateHash(transactionHash)}</span>
                            </div>
                            <button
                                onClick={() => window.open(`${EXPLORER_URL}${transactionHash}`, '_blank')}
                            >

                                      {theme === 'dark' ? (
                            <img src="/assets/whitetab.svg" alt="view" />
                    ) : (
                     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
  <path d="M12.25 8.08333V11.5833C12.25 11.8928 12.1271 12.1895 11.9083 12.4083C11.6895 12.6271 11.3928 12.75 11.0833 12.75H2.91667C2.60725 12.75 2.3105 12.6271 2.09171 12.4083C1.87292 12.1895 1.75 11.8928 1.75 11.5833V3.41667C1.75 3.10725 1.87292 2.8105 2.09171 2.59171C2.3105 2.37292 2.60725 2.25 2.91667 2.25H6.41667" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.25 2.25L7 7.5" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.75 2.25H12.25V5.75" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                    )}
                                View
                            </button>
                        </div>
                        <div className="buton_modal_div">
                            <button onClick={handleFinalClose}>Close</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ExtendDurationModals;